/* ---------------------------------------------------------------------------
   TRAWOC — pincode delivery estimate
   Reads its configuration from a JSON script tag written by the theme editor
   block, so every string, rule and cutoff is merchant-editable.

   Lead times come from the merchant's own prefix rules. There is no courier
   API behind this: if a pincode matches no rule it falls back to the default
   lead time, and unserviceable prefixes are declared explicitly.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var STORAGE_KEY = 'trawoc:pincode';

  function shopNow(offsetMinutes) {
    var d = new Date();
    if (typeof offsetMinutes !== 'number' || isNaN(offsetMinutes)) return d;
    return new Date(d.getTime() + (offsetMinutes + d.getTimezoneOffset()) * 60000);
  }

  function isoDay(d) {
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  function isWorkingDay(d, cfg) {
    if (cfg.skipSundays && d.getDay() === 0) return false;
    if (cfg.skipSaturdays && d.getDay() === 6) return false;
    return cfg.holidays.indexOf(isoDay(d)) === -1;
  }

  /* The moment the next dispatch leaves the warehouse. */
  function nextDispatch(cfg) {
    var now = shopNow(cfg.shopOffset);
    var d = new Date(now.getTime());
    d.setHours(cfg.cutoffHour, cfg.cutoffMinute, 0, 0);
    if (now >= d) d.setDate(d.getDate() + 1);
    while (!isWorkingDay(d, cfg)) d.setDate(d.getDate() + 1);
    return d;
  }

  function addWorkingDays(from, days, cfg) {
    var d = new Date(from.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (isWorkingDay(d, cfg)) added++;
    }
    return d;
  }

  /* Longest matching prefix wins, so 682301 beats 682 beats 68. */
  function matchRule(pin, cfg) {
    var best = null;
    for (var i = 0; i < cfg.rules.length; i++) {
      var r = cfg.rules[i];
      if (pin.indexOf(r.prefix) === 0) {
        if (!best || r.prefix.length > best.prefix.length) best = r;
      }
    }
    return best;
  }

  function isUnserviceable(pin, cfg) {
    for (var i = 0; i < cfg.unserviceable.length; i++) {
      if (pin.indexOf(cfg.unserviceable[i]) === 0) return true;
    }
    return false;
  }

  function countdownTo(target, cfg) {
    var ms = target - shopNow(cfg.shopOffset);
    if (ms <= 0) return null;
    var h = Math.floor(ms / 3600000);
    var m = Math.floor((ms % 3600000) / 60000);
    if (h === 0) return m + (m === 1 ? ' min' : ' mins');
    return h + (h === 1 ? ' hr ' : ' hrs ') + m + (m === 1 ? ' min' : ' mins');
  }

  function formatDate(d) {
    return DAYS[d.getDay()] + ', ' + d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  function init(root) {
    if (root.dataset.twPinReady === '1') return;
    root.dataset.twPinReady = '1';

    var cfgEl = root.querySelector('[data-tw-pin-config]');
    if (!cfgEl) return;

    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

    cfg.rules = cfg.rules || [];
    cfg.unserviceable = cfg.unserviceable || [];
    cfg.holidays = cfg.holidays || [];

    var input = root.querySelector('[data-tw-pin-input]');
    var button = root.querySelector('[data-tw-pin-submit]');
    var errEl = root.querySelector('[data-tw-pin-error]');
    var outEl = root.querySelector('[data-tw-pin-output]');
    var etaEl = root.querySelector('[data-tw-pin-eta]');
    var cutEl = root.querySelector('[data-tw-pin-cutoff]');
    var timerEl = root.querySelector('[data-tw-pin-timer]');
    var payEl = root.querySelector('[data-tw-pin-payment]');
    var timerId = null;

    if (!input || !button || !outEl) return;

    function fail(message) {
      if (errEl) {
        errEl.textContent = message;
        errEl.classList.add('is-on');
      }
      outEl.classList.remove('is-on');
      if (timerId) { clearInterval(timerId); timerId = null; }
    }

    function check() {
      var pin = (input.value || '').trim();

      if (!/^[1-9][0-9]{5}$/.test(pin)) return fail(cfg.text.invalid);
      if (errEl) errEl.classList.remove('is-on');

      if (isUnserviceable(pin, cfg)) return fail(cfg.text.unserviceable);

      var rule = matchRule(pin, cfg);
      var lead = rule ? rule.days : cfg.defaultDays;

      var dispatch = nextDispatch(cfg);
      var eta = addWorkingDays(dispatch, lead, cfg);

      if (etaEl) {
        etaEl.textContent = cfg.text.etaPrefix + ' ' + formatDate(eta) +
          (rule && rule.label ? ' · ' + rule.label : '');
      }

      if (payEl) {
        payEl.textContent = lead <= cfg.codMaxDays ? cfg.text.cod : cfg.text.prepaid;
      }

      if (cutEl && timerEl && cfg.showCountdown) {
        var tick = function () {
          var left = countdownTo(dispatch, cfg);
          if (!left) {
            cutEl.classList.remove('is-on');
            if (timerId) { clearInterval(timerId); timerId = null; }
            return;
          }
          timerEl.textContent = left;
          cutEl.classList.add('is-on');
        };
        tick();
        if (timerId) clearInterval(timerId);
        timerId = setInterval(tick, 30000);
      }

      outEl.classList.add('is-on');

      try { localStorage.setItem(STORAGE_KEY, pin); } catch (e) { /* private mode */ }
    }

    button.addEventListener('click', check);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); check(); }
    });
    input.addEventListener('input', function () {
      if (errEl) errEl.classList.remove('is-on');
    });

    /* Remember the shopper's pincode across products. */
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && !input.value) { input.value = saved; check(); }
    } catch (e) { /* private mode */ }
  }

  function boot() {
    document.querySelectorAll('[data-tw-pin]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders blocks without a page load. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', boot);
})();
